import { csrfHeaders, forgetCsrfToken, toApiError } from '@/lib/http';

/** The glossary: how a term is rendered, per language. */

export interface GlossaryEntry {
  readonly id: string;
  readonly language: string;
  readonly termSource: string;
  readonly preferredRendering: string | null;
  readonly doNotTranslate: boolean;
  readonly transliteration: string | null;
  readonly note: string | null;
  readonly updatedByName: string | null;
  readonly updatedAt: string | null;
}

export interface Divergence {
  readonly khutbahId: string;
  readonly titleAr: string | null;
  readonly language: string;
  readonly termSource: string;
  readonly preferredRendering: string;
  readonly passages: number;
}

function str(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export async function fetchGlossary(
  language: string | null,
  signal: AbortSignal,
): Promise<GlossaryEntry[]> {
  const url = language
    ? `/api/editor/glossary?language=${encodeURIComponent(language)}`
    : '/api/editor/glossary';
  const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw await toApiError(response);
  const body: unknown = await response.json();
  if (!Array.isArray(body)) return [];
  return body.flatMap((entry): GlossaryEntry[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const row = entry as Record<string, unknown>;
    const id = str(row, 'id');
    const termSource = str(row, 'termSource');
    if (id === null || termSource === null) return [];
    return [
      {
        id,
        language: str(row, 'language') ?? '',
        termSource,
        preferredRendering: str(row, 'preferredRendering'),
        doNotTranslate: row.doNotTranslate === true,
        transliteration: str(row, 'transliteration'),
        note: str(row, 'note'),
        updatedByName: str(row, 'updatedByName'),
        updatedAt: str(row, 'updatedAt'),
      },
    ];
  });
}

export async function fetchAdherence(
  language: string | null,
  signal: AbortSignal,
): Promise<Divergence[]> {
  const url = language
    ? `/api/editor/glossary/adherence?language=${encodeURIComponent(language)}`
    : '/api/editor/glossary/adherence';
  const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw await toApiError(response);
  const body: unknown = await response.json();
  if (!Array.isArray(body)) return [];
  return body.flatMap((entry): Divergence[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const row = entry as Record<string, unknown>;
    const termSource = str(row, 'termSource');
    const rendering = str(row, 'preferredRendering');
    if (termSource === null || rendering === null) return [];
    return [
      {
        khutbahId: str(row, 'khutbahId') ?? '',
        titleAr: str(row, 'titleAr'),
        language: str(row, 'language') ?? '',
        termSource,
        preferredRendering: rendering,
        passages: typeof row.passages === 'number' ? row.passages : 0,
      },
    ];
  });
}

export async function agreeTerm(input: {
  language: string;
  termSource: string;
  preferredRendering: string;
  doNotTranslate: boolean;
  transliteration: string;
  note: string;
}): Promise<string> {
  const csrf = await csrfHeaders(new AbortController().signal);
  const response = await fetch('/api/editor/glossary', {
    method: 'POST',
    headers: { ...csrf, Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) forgetCsrfToken();
    throw await toApiError(response);
  }
  const body: unknown = await response.json();
  const result = (typeof body === 'object' && body !== null ? body : {}) as Record<
    string,
    unknown
  >;
  return typeof result.note === 'string' ? result.note : 'Agreed.';
}
